'use client';

import { useEffect, useState } from 'react';
import type * as RPNINamespace from 'react-phone-number-input';
import type RPNIDefault from 'react-phone-number-input';
import type FlagsDefault from 'react-phone-number-input/flags';

export interface PhoneInputDeps {
  PhoneNumberInput: typeof RPNIDefault;
  getCountryCallingCode: (typeof RPNINamespace)['getCountryCallingCode'];
  flags: typeof FlagsDefault;
}

let cached: PhoneInputDeps | null = null;
let loadPromise: Promise<PhoneInputDeps> | null = null;

/**
 * Lazy-load `react-phone-number-input` + `react-phone-number-input/flags`.
 * Wrapping the dynamic imports in a function keeps the combined ~264 KB
 * parsed (`country-flag-icons` flag SVGs + the RPNI UI components) out of
 * `_app.js`'s static-import graph (ARC-386) — webpack puts both packages in
 * dedicated async chunks that only resolve when a `<PhoneInput>` first
 * mounts.
 *
 * The first call returns the in-flight Promise; subsequent calls reuse the
 * cached module bundle.
 */
export const loadPhoneInputDeps = (): Promise<PhoneInputDeps> => {
  if (cached) return Promise.resolve(cached);
  loadPromise ??= Promise.all([
    import('react-phone-number-input'),
    import('react-phone-number-input/flags'),
  ]).then(([rpni, flagsModule]) => {
    const resolved: PhoneInputDeps = {
      PhoneNumberInput: rpni.default,
      getCountryCallingCode: rpni.getCountryCallingCode,
      flags: flagsModule.default,
    };
    cached = resolved;
    return resolved;
  });
  return loadPromise;
};

/**
 * Returns the cached phone-input deps or `null` if they haven't loaded yet.
 * Use inside subcomponents that only render *after* `<PhoneInput>` has
 * resolved its deps — e.g. the `flagComponent` / `countrySelectComponent`
 * passed into `<PhoneNumberInput>` — where the value is guaranteed to be
 * non-null. Pair with `usePhoneInputDeps()` at the top-level component so
 * the tree re-renders once the chunks resolve.
 */
export const getPhoneInputDepsSync = (): PhoneInputDeps | null => cached;

/**
 * React hook that triggers a lazy load of the `react-phone-number-input`
 * package + flags chunk on mount and returns the deps once available
 * (`null` until then). The top-level `<PhoneInput>` wrapper gates its
 * `<PhoneNumberInput>` render on this so the heavy UI never mounts until
 * the async chunks resolve.
 */
export function usePhoneInputDeps(): PhoneInputDeps | null {
  const [deps, setDeps] = useState<PhoneInputDeps | null>(cached);

  useEffect(() => {
    if (deps) return;

    let cancelled = false;

    void loadPhoneInputDeps().then((d) => {
      if (!cancelled) setDeps(d);
    });

    return () => {
      cancelled = true;
    };
  }, [deps]);

  return deps;
}
